import React, {
    useMemo,
    useState,
} from 'react'

import PulseLoader from 'react-spinners/PulseLoader'

import {
    useGetMySharedCoachEvaluationsQuery,
} from '@/features/coachUserSessionEvals/coachUserSessionEvalSharesApiSlice'


/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
|
| Only participant-approved sharing records are used.
|
| No private evaluation matrix is retrieved here.
|
|--------------------------------------------------------------------------
*/

const getId = value => {

    if (!value) return ''

    if (typeof value === 'string') {
        return value
    }

    return String(value._id || value.id || '')

}


const formatScore = value => {

    if (
        typeof value !== 'number' ||
        !Number.isFinite(value)
    ) {
        return '—'
    }

    return value
        .toFixed(2)
        .replace(/\.?0+$/, '')

}


const formatDate = value => {

    if (!value) return '—'

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return '—'
    }

    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })

}


const formatDateTime = value => {

    if (!value) return '—'

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
        return '—'
    }

    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })

}


/*
|--------------------------------------------------------------------------
| 02 - Circular Evaluation Score
|--------------------------------------------------------------------------
*/

const SharedScoreGauge = ({
    score,
    size = 60,
    color = '#ea580c',
}) => {

    const valid =
        typeof score === 'number' &&
        Number.isFinite(score) &&
        score >= 1 &&
        score <= 5

    const radius = 40

    const circumference =
        2 * Math.PI * radius

    const offset = valid
        ? circumference * (1 - score / 5)
        : circumference

    return (

        <div
            className="relative inline-flex shrink-0 items-center justify-center"
            style={{
                width: size,
                height: size,
            }}
            role="img"
            aria-label={
                valid
                    ? `Score ${formatScore(score)} out of 5`
                    : 'Score unavailable'
            }
        >

            <svg
                viewBox="0 0 100 100"
                width={size}
                height={size}
                aria-hidden="true"
            >

                <circle
                    cx="50"
                    cy="50"
                    r={radius}
                    fill="none"
                    stroke="#fed7aa"
                    strokeWidth="8"
                />

                {valid && (

                    <circle
                        cx="50"
                        cy="50"
                        r={radius}
                        fill="none"
                        stroke={color}
                        strokeWidth="8"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        transform="rotate(-90 50 50)"
                    />

                )}

            </svg>

            <div className="absolute inset-0 flex flex-col items-center justify-center">

                <span className="text-sm font-bold leading-none text-orange-700">
                    {formatScore(score)}
                </span>

                <span className="mt-0.5 text-[9px] text-gray-400">
                    / 5
                </span>

            </div>

        </div>

    )

}


/*
|--------------------------------------------------------------------------
| 03 - Evaluation Score Scale
|--------------------------------------------------------------------------
*/

const SharedScoreScale = ({
    score,
    highlight = false,
}) => {

    const valid =
        typeof score === 'number' &&
        Number.isFinite(score) &&
        score >= 1 &&
        score <= 5

    if (!valid) {
        return (
            <span className="text-xs text-gray-300">
                —
            </span>
        )
    }

    const position =
        (score - 1) / 4

    return (

        <div
            className="flex min-w-[112px] items-center gap-2"
            aria-label={`Score ${formatScore(score)} out of 5`}
        >

            <div className="relative min-w-[64px] flex-1 px-[6px]">

                <div className="relative h-5">

                    <div
                        className="absolute inset-x-0 top-1/2 h-[5px] -translate-y-1/2 rounded-full"
                        style={{
                            background:
                                'linear-gradient(to right, #fda4af, #fcd34d, #bef264, #5eead4)',
                        }}
                    />

                    <div
                        className={`absolute top-1/2 h-[12px] w-[12px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-sm ${
                            highlight
                                ? 'bg-orange-600 ring-2 ring-orange-200'
                                : 'bg-teal-700'
                        }`}
                        style={{
                            left: `calc(6px + (100% - 12px) * ${position})`,
                        }}
                    />

                </div>

            </div>

            <span
                className={`w-6 text-right text-[10px] font-bold ${
                    highlight
                        ? 'text-orange-600'
                        : 'text-teal-700'
                }`}
            >
                {formatScore(score)}
            </span>

        </div>

    )

}


/*
|--------------------------------------------------------------------------
| 04 - View Tabs
|--------------------------------------------------------------------------
*/

const ViewTab = ({
    active,
    onClick,
    children,
}) => (

    <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={onClick}
        className={`rounded-t px-4 py-2 text-[11px] font-bold ${
            active
                ? 'border-b-2 border-orange-600 bg-orange-50 text-orange-600'
                : 'text-gray-500 hover:bg-orange-50/40'
        }`}
    >
        {children}
    </button>

)


/*
|--------------------------------------------------------------------------
| 05 - Participant Coach Evaluation Board
|--------------------------------------------------------------------------
|
| First Created - 29 Oct 2026
|
| Step 11E - Participant Evaluation Visibility
|
| Data Source:
| getMySharedEvaluations
|
| The backend resolves participant identity from JWT.
| This component must never query private coach matrices.
|
|--------------------------------------------------------------------------
*/

const EtUserSessionCoachEvaluationsBoard = ({

    orgId,
    trailId,
    sessionId,
    cohortId,

}) => {

    // First Created - 29 Oct 2026
    // Author - Atul Pandey

    /*
    |--------------------------------------------------------------------------
    | 06 - Local View Controls
    |--------------------------------------------------------------------------
    */

    const [
        activeView,
        setActiveView,
    ] = useState('progress')

    const [
        selectedCategoryId,
        setSelectedCategoryId,
    ] = useState('all')

    const [
        selectedEvaluationId,
        setSelectedEvaluationId,
    ] = useState(null)


    /*
    |--------------------------------------------------------------------------
    | 07 - Load Approved Evaluations
    |--------------------------------------------------------------------------
    |
    | No userId is sent.
    |
    | Only the authenticated participant's
    | currently shared snapshots are returned.
    |
    */

    const queryArgs = useMemo(() => ({

        ...(orgId && {
            orgId: getId(orgId),
        }),

        ...(trailId && {
            trailId: getId(trailId),
        }),

        ...(sessionId && {
            sessionId: getId(sessionId),
        }),

        ...(cohortId && {
            cohortId: getId(cohortId),
        }),

    }), [
        orgId,
        trailId,
        sessionId,
        cohortId,
    ])


    const {
        data: sharingData,
        isLoading,
        isFetching,
        isError,
        refetch,
    } = useGetMySharedCoachEvaluationsQuery(
        queryArgs,
        {
            skip:
                !orgId ||
                !trailId ||
                !sessionId,
            refetchOnMountOrArgChange: true,
        }
    )


    /*
    |--------------------------------------------------------------------------
    | 08 - Chronological Approved Evaluations
    |--------------------------------------------------------------------------
    |
    | Every returned item is already an approved
    | publication snapshot.
    |
    */

    const evaluations = useMemo(() => {

        return (
            Array.isArray(sharingData)
                ? sharingData
                : []
        )
            .slice()
            .sort((a, b) => {

                const dateDifference =
                    new Date(a.evaluatedAt).getTime() -
                    new Date(b.evaluatedAt).getTime()

                return (
                    dateDifference ||
                    String(a.shareId).localeCompare(
                        String(b.shareId)
                    )
                )

            })

    }, [sharingData])


    /*
    |--------------------------------------------------------------------------
    | 09 - Evaluation Dimension History
    |--------------------------------------------------------------------------
    |
    | The same dimension may occur in multiple approved
    | evaluation snapshots.
    |
    | Only published dimensions enter this history.
    |
    */

    const dimensionHistory = useMemo(() => {

        const map = new Map()

        evaluations.forEach(evaluation => {

            ;(evaluation.dimensions || []).forEach(item => {

                const dimensionId = getId(
                    item.dimensionId
                )

                if (!dimensionId) return

                if (!map.has(dimensionId)) {

                    map.set(dimensionId, {

                        dimensionId,

                        categoryId: getId(
                            item.categoryId
                        ),

                        categoryName:
                            item.categoryName ||
                            'Uncategorized',

                        dimensionName:
                            item.dimensionName ||
                            'Evaluation Dimension',

                        observations: [],

                    })

                }

                const entry =
                    map.get(dimensionId)

                entry.observations.push({

                    shareId:
                        evaluation.shareId,

                    evaluatedAt:
                        evaluation.evaluatedAt,

                    score:
                        item.score,

                })

            })

        })

        return [...map.values()]
            .sort((a, b) => {

                return (
                    a.categoryName.localeCompare(
                        b.categoryName
                    ) ||
                    a.dimensionName.localeCompare(
                        b.dimensionName
                    )
                )

            })

    }, [evaluations])


    /*
    |--------------------------------------------------------------------------
    | 10 - Category Filters
    |--------------------------------------------------------------------------
    */

    const categories = useMemo(() => {

        const map = new Map()

        dimensionHistory.forEach(item => {

            const key =
                item.categoryId ||
                `uncategorized:${item.categoryName}`

            if (!map.has(key)) {

                map.set(key, {

                    id: key,

                    name: item.categoryName,

                })

            }

        })

        return [...map.values()]
            .sort(
                (a, b) =>
                    a.name.localeCompare(b.name)
            )

    }, [dimensionHistory])


    const effectiveCategoryId =
        selectedCategoryId === 'all' ||
        categories.some(
            item => item.id === selectedCategoryId
        )
            ? selectedCategoryId
            : 'all'


    const visibleDimensions = useMemo(() => {

        return dimensionHistory.filter(item => {

            const key =
                item.categoryId ||
                `uncategorized:${item.categoryName}`

            return (
                effectiveCategoryId === 'all' ||
                key === effectiveCategoryId
            )

        })

    }, [
        dimensionHistory,
        effectiveCategoryId,
    ])


    /*
    |--------------------------------------------------------------------------
    | 11 - Coach Feedback
    |--------------------------------------------------------------------------
    |
    | A ProgressOnly snapshot never shows feedback.
    |
    | Only explicit sharedFeedback is displayed.
    |
    */

    const feedbackEvaluations = useMemo(() => {

        return evaluations
            .filter(
                item =>
                    item.sharingMode ===
                    'ProgressAndFeedback' &&
                    (item.dimensions || []).some(
                        dimension =>
                            Boolean(
                                dimension.sharedFeedback?.trim()
                            )
                    )
            )
            .slice()
            .reverse()

    }, [evaluations])


    const activeFeedbackEvaluation =
        feedbackEvaluations.find(
            item =>
                item.shareId === selectedEvaluationId
        ) ||
        feedbackEvaluations[0] ||
        null


    /*
    |--------------------------------------------------------------------------
    | 12 - Loading And Error States
    |--------------------------------------------------------------------------
    */

    if (
        !orgId ||
        !trailId ||
        !sessionId
    ) {

        return (

            <p className="p-3 text-xs text-gray-500">
                Select a session to view shared evaluations.
            </p>

        )

    }

    if (isLoading) {

        return (

            <div className="flex justify-center p-5">

                <PulseLoader
                    color="#ea580c"
                    size={7}
                />

            </div>

        )

    }

    if (isError) {

        return (

            <div className="rounded border border-rose-200 bg-rose-50 p-3">

                <p className="text-xs text-rose-700">
                    Unable to load shared coach evaluations.
                </p>

                <button
                    type="button"
                    onClick={() => refetch()}
                    className="mt-2 text-[10px] font-bold text-rose-700 underline"
                >
                    Try Again
                </button>

            </div>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 13 - Participant Workspace
    |--------------------------------------------------------------------------
    */

    return (

        <div className="rounded-lg border border-orange-200 bg-white p-3 font-poppins">

            {/*----------------------------------------------------------
              Header
            ----------------------------------------------------------*/}

            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-orange-100 pb-3">

                <div>

                    <p className="text-sm font-bold text-orange-600">
                        My Coach Evaluations
                    </p>

                    <p className="mt-1 text-[10px] text-gray-500">
                        Development progress and feedback
                        shared with me by my coach.
                    </p>

                </div>

                <button
                    type="button"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="rounded-full border border-orange-200 px-3 py-1 text-[10px] font-semibold text-orange-600 hover:bg-orange-50 disabled:opacity-50"
                >
                    {isFetching
                        ? 'Refreshing...'
                        : 'Refresh'}
                </button>

            </div>


            {/*----------------------------------------------------------
              No Shared Evaluations
            ----------------------------------------------------------*/}

            {evaluations.length === 0 ? (

                <div className="mt-4 rounded-lg border border-dashed border-orange-200 bg-orange-50/40 p-6 text-center">

                    <p className="text-xs font-semibold text-gray-600">
                        No Coach Evaluations Shared Yet
                    </p>

                    <p className="mt-2 text-[10px] text-gray-500">
                        Evaluations will appear here when
                        your coach chooses to share them.
                    </p>

                </div>

            ) : (

                <>

                    {/*--------------------------------------------------
                      Workspace Tabs
                    --------------------------------------------------*/}

                    <div
                        role="tablist"
                        aria-label="Shared evaluation views"
                        className="mt-3 flex gap-2 border-b border-orange-100"
                    >

                        <ViewTab
                            active={
                                activeView === 'progress'
                            }
                            onClick={() =>
                                setActiveView('progress')
                            }
                        >
                            My Progress
                        </ViewTab>

                        <ViewTab
                            active={
                                activeView === 'feedback'
                            }
                            onClick={() =>
                                setActiveView('feedback')
                            }
                        >
                            Coach Feedback
                        </ViewTab>

                    </div>


                    {/*--------------------------------------------------
                      My Progress View
                    --------------------------------------------------*/}

                    {activeView === 'progress' && (

                        <div className="mt-4">

                            <div className="mb-3 flex flex-wrap gap-2">

                                <button
                                    type="button"
                                    onClick={() =>
                                        setSelectedCategoryId('all')
                                    }
                                    className={`rounded-full border px-3 py-1 text-[10px] font-semibold ${
                                        effectiveCategoryId === 'all'
                                            ? 'border-orange-600 bg-orange-600 text-white'
                                            : 'border-gray-200 bg-white text-gray-600'
                                    }`}
                                >
                                    All Categories
                                </button>

                                {categories.map(category => (

                                    <button
                                        key={category.id}
                                        type="button"
                                        onClick={() =>
                                            setSelectedCategoryId(
                                                category.id
                                            )
                                        }
                                        className={`rounded-full border px-3 py-1 text-[10px] font-semibold ${
                                            effectiveCategoryId ===
                                            category.id
                                                ? 'border-orange-600 bg-orange-600 text-white'
                                                : 'border-gray-200 bg-white text-gray-600'
                                        }`}
                                    >
                                        {category.name}
                                    </button>

                                ))}

                            </div>


                            {/* Evaluation Matrix */}

                            <div className="overflow-hidden rounded-lg border border-orange-100">

                                <div className="max-h-[500px] overflow-auto">

                                    <table className="w-full min-w-max border-separate border-spacing-0 text-left">

                                        <thead className="sticky top-0 z-30 bg-orange-50">

                                            <tr>

                                                <th className="sticky left-0 z-40 w-36 min-w-36 border-b border-r border-orange-100 bg-orange-50 p-3 text-[10px] font-bold text-gray-700">
                                                    Category
                                                </th>

                                                <th className="sticky left-36 z-40 w-48 min-w-48 border-b border-r border-orange-100 bg-orange-50 p-3 text-[10px] font-bold text-gray-700">
                                                    Dimension
                                                </th>

                                                <th className="sticky left-[336px] z-40 min-w-36 border-b border-r-2 border-orange-200 bg-orange-100 p-3 text-[10px] font-bold text-orange-700">
                                                    Latest Shared
                                                </th>

                                                {evaluations.map(
                                                    (evaluation, index) => (

                                                        <th
                                                            key={evaluation.shareId}
                                                            className="min-w-36 border-b border-r border-orange-100 p-3 text-[10px] font-bold text-gray-700"
                                                            title={
                                                                formatDateTime(
                                                                    evaluation.evaluatedAt
                                                                )
                                                            }
                                                        >
                                                            <p>
                                                                Eval {index + 1}
                                                            </p>

                                                            <p className="mt-1 text-[9px] font-normal text-gray-500">
                                                                {formatDate(
                                                                    evaluation.evaluatedAt
                                                                )}
                                                            </p>
                                                        </th>

                                                    )
                                                )}

                                            </tr>

                                        </thead>

                                        <tbody>

                                            {visibleDimensions.map(
                                                (dimension, index) => {

                                                    const latest =
                                                        dimension.observations[
                                                            dimension.observations.length - 1
                                                        ]

                                                    return (

                                                        <tr
                                                            key={dimension.dimensionId}
                                                            className={
                                                                index % 2 === 0
                                                                    ? 'bg-white'
                                                                    : 'bg-sky-50/30'
                                                            }
                                                        >

                                                            <td className={`sticky left-0 z-20 border-b border-r border-gray-100 p-3 text-[10px] text-orange-700 ${
                                                                index % 2 === 0
                                                                    ? 'bg-white'
                                                                    : 'bg-sky-50'
                                                            }`}>
                                                                {dimension.categoryName}
                                                            </td>

                                                            <td className={`sticky left-36 z-20 border-b border-r border-gray-100 p-3 text-[11px] font-semibold text-gray-700 ${
                                                                index % 2 === 0
                                                                    ? 'bg-white'
                                                                    : 'bg-sky-50'
                                                            }`}>
                                                                {dimension.dimensionName}
                                                            </td>

                                                            <td className="sticky left-[336px] z-20 border-b border-r-2 border-orange-200 bg-orange-50 p-3">

                                                                <SharedScoreScale
                                                                    score={latest?.score}
                                                                    highlight
                                                                />

                                                            </td>

                                                            {evaluations.map(
                                                                evaluation => {

                                                                    const observation =
                                                                        dimension.observations.find(
                                                                            item =>
                                                                                item.shareId ===
                                                                                evaluation.shareId
                                                                        )

                                                                    return (

                                                                        <td
                                                                            key={`${dimension.dimensionId}-${evaluation.shareId}`}
                                                                            className="border-b border-r border-gray-100 p-3"
                                                                        >

                                                                            <SharedScoreScale
                                                                                score={
                                                                                    observation?.score
                                                                                }
                                                                            />

                                                                        </td>

                                                                    )

                                                                }
                                                            )}

                                                        </tr>

                                                    )

                                                }
                                            )}

                                        </tbody>

                                    </table>

                                </div>

                            </div>


                            <p className="mt-3 text-[10px] text-gray-500">
                                Scores shown here are from evaluations
                                your coach has approved for sharing.
                                The scale ranges from 1 to 5.
                            </p>

                        </div>

                    )}


                    {/*--------------------------------------------------
                      Coach Feedback View
                    --------------------------------------------------*/}

                    {activeView === 'feedback' && (

                        <div className="mt-4">

                            {feedbackEvaluations.length === 0 ? (

                                <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center">

                                    <p className="text-xs text-gray-500">
                                        No written coach feedback has
                                        been shared for this session.
                                    </p>

                                </div>

                            ) : (

                                <>

                                    {/* Evaluation Tabs */}

                                    <div
                                        role="tablist"
                                        aria-label="Shared coach feedback"
                                        className="flex gap-2 overflow-x-auto rounded-lg bg-violet-50/50 p-2"
                                    >

                                        {feedbackEvaluations.map(
                                            evaluation => {

                                                const originalIndex =
                                                    evaluations.findIndex(
                                                        item =>
                                                            item.shareId ===
                                                            evaluation.shareId
                                                    )

                                                const selected =
                                                    activeFeedbackEvaluation?.shareId ===
                                                    evaluation.shareId

                                                return (

                                                    <button
                                                        key={evaluation.shareId}
                                                        type="button"
                                                        role="tab"
                                                        aria-selected={selected}
                                                        onClick={() =>
                                                            setSelectedEvaluationId(
                                                                evaluation.shareId
                                                            )
                                                        }
                                                        className={`shrink-0 rounded-lg border px-4 py-2 text-left ${
                                                            selected
                                                                ? 'border-orange-500 bg-orange-600 text-white'
                                                                : 'border-gray-200 bg-white text-gray-600'
                                                        }`}
                                                    >

                                                        <p className="text-[11px] font-bold">
                                                            Evaluation {
                                                                originalIndex + 1
                                                            }
                                                        </p>

                                                        <p className="mt-1 text-[9px] opacity-80">
                                                            {formatDate(
                                                                evaluation.evaluatedAt
                                                            )}
                                                        </p>

                                                    </button>

                                                )

                                            }
                                        )}

                                    </div>


                                    {/* Selected Feedback */}

                                    {activeFeedbackEvaluation && (

                                        <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/40 p-3">

                                            <p className="text-[11px] font-bold text-violet-700">
                                                Coach Feedback
                                            </p>

                                            <p className="mt-1 text-[10px] text-gray-500">
                                                {formatDateTime(
                                                    activeFeedbackEvaluation.evaluatedAt
                                                )}
                                            </p>

                                            <div className="mt-3 space-y-2">

                                                {activeFeedbackEvaluation.dimensions
                                                    .filter(
                                                        item =>
                                                            Boolean(
                                                                item.sharedFeedback?.trim()
                                                            )
                                                    )
                                                    .map(item => (

                                                        <div
                                                            key={getId(item.dimensionId)}
                                                            className="rounded border border-violet-100 bg-white p-3"
                                                        >

                                                            <div className="flex items-center justify-between gap-3">

                                                                <div>

                                                                    <p className="text-[9px] text-gray-400">
                                                                        {item.categoryName}
                                                                    </p>

                                                                    <p className="mt-1 text-[11px] font-bold text-gray-700">
                                                                        {item.dimensionName}
                                                                    </p>

                                                                </div>

                                                                <SharedScoreGauge
                                                                    score={item.score}
                                                                    size={52}
                                                                />

                                                            </div>

                                                            <p className="mt-3 whitespace-pre-wrap rounded border-l-2 border-orange-300 bg-orange-50/60 p-3 text-[11px] leading-5 text-gray-700">
                                                                {item.sharedFeedback}
                                                            </p>

                                                        </div>

                                                    )
                                                )}

                                            </div>

                                        </div>

                                    )}

                                </>

                            )}

                        </div>

                    )}

                </>

            )}

        </div>

    )

}


export default EtUserSessionCoachEvaluationsBoard